import { useTranslation } from 'react-i18next';
import { TableScroll } from '@hp/ui';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { fmtInt } from '../../lib/format';
import type { ChannelSpend, CohortMetrics } from './queries';

export interface CohortRow {
  key: string;
  label: string;
  /** Secondary line under the label (e.g. the CRM sources behind a channel). */
  detail?: string;
  metrics: CohortMetrics;
  /** Media spend of the row's channel in the period; null/undefined = no paid media for this row. */
  spend?: ChannelSpend | null;
}

function pct(n: number, of: number): string {
  if (of <= 0) return '—';
  return `${((n / of) * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })} %`;
}

function MetricCells({ m }: { m: CohortMetrics }) {
  const lost = m.lostByRank.reduce((a, b) => a + b, 0);
  return (
    <>
      <td className="bo-num">{fmtInt(m.leads)}</td>
      <td className="bo-num">
        {fmtInt(m.reached[1] ?? 0)} <small className="bo-muted">{pct(m.reached[1] ?? 0, m.leads)}</small>
      </td>
      <td className="bo-num">{fmtInt(m.reached[2] ?? 0)}</td>
      <td className="bo-num">{fmtInt(m.reached[3] ?? 0)}</td>
      <td className="bo-num">{fmtInt(m.reached[4] ?? 0)}</td>
      <td className="bo-num">{fmtInt(m.reached[5] ?? 0)}</td>
      <td className="bo-num">
        <strong>{fmtInt(m.reserved)}</strong>
        {m.cancelled > 0 && <small className="bo-muted"> −{fmtInt(m.cancelled)}</small>}
      </td>
      <td className="bo-num">{fmtInt(Math.round(m.valueCzk))}</td>
      <td className="bo-num">
        {fmtInt(m.noContact)} <small className="bo-muted">{pct(m.noContact, m.leads)}</small>
      </td>
      <td className="bo-num">{fmtInt(lost)}</td>
    </>
  );
}

/** Spend, cost per case and cost per reservation; a row without paid media shows dashes. */
function SpendCells({ s, m }: { s: ChannelSpend | null | undefined; m: CohortMetrics }) {
  if (!s) {
    return (
      <>
        <td className="bo-num bo-muted">—</td>
        <td className="bo-num bo-muted">—</td>
        <td className="bo-num bo-muted">—</td>
      </>
    );
  }
  return (
    <>
      <td className="bo-num">
        {fmtInt(Math.round(s.costCzk))}
        {s.hasUnconverted && <small className="bo-muted"> +</small>}
      </td>
      <td className="bo-num">{m.leads > 0 ? fmtInt(Math.round(s.costCzk / m.leads)) : '—'}</td>
      <td className="bo-num">{m.reserved > 0 ? fmtInt(Math.round(s.costCzk / m.reserved)) : '—'}</td>
    </>
  );
}

/** One cohort breakdown (channels, pipelines or projects) with a totals row; every cell is a case count. */
export function CohortTable({
  rows,
  totals,
  label,
  showSpend = false,
  totalsSpend,
}: {
  rows: CohortRow[];
  totals: CohortMetrics;
  label: string;
  /** Add the media cost columns (only the channel view can attribute spend). */
  showSpend?: boolean;
  totalsSpend?: ChannelSpend | null;
}) {
  const { t } = useTranslation(['performance', 'common']);
  const columns = showSpend ? 14 : 11;
  return (
    <TableScroll label={label}>
      <BrutTable className="bo-cohort-table">
        <BrutTheadRow>
          <th>{t('table.group')}</th>
          <th className="bo-num">{t('table.leads')}</th>
          <th className="bo-num">{t('table.rank2')}</th>
          <th className="bo-num">{t('table.rank3')}</th>
          <th className="bo-num">{t('table.rank4')}</th>
          <th className="bo-num">{t('table.rank5')}</th>
          <th className="bo-num">{t('table.rank6')}</th>
          <th className="bo-num">{t('table.reserved')}</th>
          <th className="bo-num">{t('table.value')}</th>
          <th className="bo-num">{t('table.noContact')}</th>
          <th className="bo-num">{t('table.lost')}</th>
          {showSpend && (
            <>
              <th className="bo-num">{t('table.spend')}</th>
              <th className="bo-num">{t('table.costPerLead')}</th>
              <th className="bo-num">{t('table.costPerReserved')}</th>
            </>
          )}
        </BrutTheadRow>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>
                <span className="bo-strong">{r.label}</span>
                {r.detail && (
                  <>
                    <br />
                    <small className="bo-muted">{r.detail}</small>
                  </>
                )}
              </td>
              <MetricCells m={r.metrics} />
              {showSpend && <SpendCells s={r.spend} m={r.metrics} />}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns} className="bo-cell-empty">
                {t('table.empty')}
              </td>
            </tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr className="bo-cohort-table__total">
              <td>{t('table.total')}</td>
              <MetricCells m={totals} />
              {showSpend && <SpendCells s={totalsSpend} m={totals} />}
            </tr>
          </tfoot>
        )}
      </BrutTable>
    </TableScroll>
  );
}
